import {useTranslation} from 'react-i18next'
import {DropdownMenu} from '../../../../../components/dropdown-menu/DropdownMenu'
import {ContextMenuIcon} from '../../../../../theme/icons'
import {UserIcon} from '../../../../../assets/icons/UserIcon'
import {NavigationIcon} from '../../../../../assets/icons/NavigationIcon'
import {SettingsIcon} from '../../../../../assets/icons/SettingsIcon'
import {ALERT_LEVELS, IS_REDIRECT_ENABLED} from 'src/helpers/constants'
import {PauseIcon} from 'src/assets/icons/PauseIcon'
import {StyledMenuItemText} from './style'
import {SuspendFreelancerModal} from '../suspend-freelancer-modal/SuspendFreelancerModal'
import {useState} from 'react'
import {PowerIcon} from 'src/assets/icons/PowerIcon'
import {DeactivateFreelancerModal} from '../deactivate-freelancer-modal/DeactivateFreelancerModal'
import {useTheme} from 'styled-components'
import {useAdminChangeFreelancerStatus} from 'src/features/admin/services/useAdminChangeFreelancerStatus'
import {Freelancer, FreelancerStatus} from 'src/features/admin/types'
import {PlayIcon} from 'src/assets/icons/PlayIcon'
import {Spinner} from 'src/components/spinner/Spinner'
import {useDispatch} from 'react-redux'
import {showAlert} from 'src/store/appGenerics'

interface FreelancerActionsDropdownProps {
    freelancer: Freelancer
    onImpersonate: (userId: string) => void
    onImpersonateNavigate: (userId: string) => void
    onEdit: (freelancer: Freelancer) => void
}
export const MAX_REASON_CHARACTERS = 500
export const FreelancerActionsDropdown = ({
    freelancer,
    onImpersonate,
    onImpersonateNavigate,
    onEdit
}: FreelancerActionsDropdownProps) => {
    const {t} = useTranslation()
    const {palette} = useTheme()
    const dispatch = useDispatch()
    const [isSuspendModalOpen, setIsSuspendModalOpen] = useState(false)
    const [isDeactivateModalOpen, setIsDeactivateModalOpen] = useState(false)
    const changeFreelancerStatusMutation = useAdminChangeFreelancerStatus(freelancer.id, {
        onSuccess: () => {
            dispatch(
                showAlert({
                    message: t('impersonate_statuses:status_changed_success'),
                    level: ALERT_LEVELS.SUCCESS
                })
            )
        }
    })

    const onActivateFreelancer = () => {
        changeFreelancerStatusMutation.mutate({status: FreelancerStatus.Enum.active_available})
    }

    const menuItemsForActiveFreelancer = [
        {
            text: <StyledMenuItemText>{t('admin:freelancers_deactivation:suspend')}</StyledMenuItemText>,
            callback: () => setIsSuspendModalOpen(true),
            icon: <PauseIcon />,
            disabled: changeFreelancerStatusMutation.isLoading
        },
        {
            text: <StyledMenuItemText>{t('admin:freelancers_deactivation:deactivate')}</StyledMenuItemText>,
            callback: () => setIsDeactivateModalOpen(true),
            icon: <PowerIcon />,
            disabled: changeFreelancerStatusMutation.isLoading
        }
    ]
    const menuItemsForSuspendedFreelancer = [
        {
            text: t('admin:freelancers_deactivation:resume'),
            callback: () => onActivateFreelancer(),
            icon: changeFreelancerStatusMutation.isLoading ? <Spinner size={16} /> : <PlayIcon />,
            disabled: changeFreelancerStatusMutation.isLoading
        },
        {
            text: <StyledMenuItemText>{t('admin:freelancers_deactivation:deactivate')}</StyledMenuItemText>,
            callback: () => setIsDeactivateModalOpen(true),
            icon: <PowerIcon />,
            disabled: changeFreelancerStatusMutation.isLoading
        }
    ]

    const menuItemsForDeactivatedFreelancer = [
        {
            text: t('admin:freelancers_deactivation:reactivate'),
            callback: () => onActivateFreelancer(),
            icon: changeFreelancerStatusMutation.isLoading ? (
                <Spinner size={16} />
            ) : (
                <PowerIcon fill={palette.neutral.black} />
            ),
            disabled: changeFreelancerStatusMutation.isLoading
        },
        {
            text: <StyledMenuItemText>{t('admin:freelancers_deactivation:suspend')}</StyledMenuItemText>,
            callback: () => setIsSuspendModalOpen(true),
            icon: <PauseIcon />,
            disabled: changeFreelancerStatusMutation.isLoading
        }
    ]

    const statusToMenuItems = {
        [FreelancerStatus.Enum.active_available]: menuItemsForActiveFreelancer,
        [FreelancerStatus.Enum.active_unavailable]: menuItemsForActiveFreelancer,
        [FreelancerStatus.Enum.suspended]: menuItemsForSuspendedFreelancer,
        [FreelancerStatus.Enum.deactivated]: menuItemsForDeactivatedFreelancer
    }

    const menuItems = [
        ...(!IS_REDIRECT_ENABLED
            ? [
                  {
                      text: t('commons:impersonate'),
                      callback: () => onImpersonate(freelancer.id),
                      icon: <UserIcon size={16} />
                  }
              ]
            : []),
        {
            text: `${t('commons:impersonate')} on Navigate`,
            callback: () => onImpersonateNavigate(freelancer.id),
            icon: <NavigationIcon size={16} />
        },
        {
            text: t('commons:edit'),
            callback: () => onEdit(freelancer),
            icon: <SettingsIcon size={16} />
        },
        {divider: true},
        ...statusToMenuItems[freelancer.status]
    ]

    return (
        <>
            {changeFreelancerStatusMutation.isLoading ? (
                <Spinner size={16} />
            ) : (
                <DropdownMenu menuItems={menuItems} trigger={<ContextMenuIcon />} />
            )}

            {isSuspendModalOpen && (
                <SuspendFreelancerModal freelancer={freelancer} onClose={() => setIsSuspendModalOpen(false)} />
            )}
            {isDeactivateModalOpen && (
                <DeactivateFreelancerModal freelancer={freelancer} onClose={() => setIsDeactivateModalOpen(false)} />
            )}
        </>
    )
}
